import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Button } from '../../components/buttons/button';
import { Input } from '../../components/ui/input';
import { ILLUSTRATIONS, ICONS } from '../../constants/imageConstants';
import AuthAlerts from '../../components/common/AuthAlerts';
import { authService } from '../../services/authService';
import { useAuthForm } from '../../hooks/useAuthForm';
import { extractErrorMessage } from '../../utils/errorUtils';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const token = search.get('token') || '';
  
  const [done, setDone] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');

  const {
    formData,
    submitting,
    error,
    setError,
    onChange,
    showPassword,
    setShowPassword,
    showConfirm,
    setShowConfirm,
    handleSubmit
  } = useAuthForm({ password: '', confirmPassword: '' }, 'reset-password');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setVerifyError('Invalid or missing token');
      return;
    }
    
    authService.verifyPasswordResetToken(token)
      .then(() => setVerifying(false))
      .catch((err) => {
        setVerifyError(extractErrorMessage(err, 'Invalid reset token'));
        setVerifying(false);
      });
  }, [token]);

  const onSubmit = (e) => {
    handleSubmit(
      e,
      () => authService.resetPassword(token, formData.password),
      () => setDone(true)
    );
  };

  if (verifying) {
    return (
      <AuthLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(var(--primary))]"></div>
        </div>
      </AuthLayout>
    );
  }

  if (verifyError) {
    return (
      <AuthLayout>
        <div className="flex justify-center mb-6">
          <AuthAlerts type="error" message={verifyError} onClear={() => setVerifyError('')} />
        </div>
        <div className="text-center">
          <Button
            variant="primary"
            className="w-80 h-12 mx-auto"
            onClick={() => navigate('/forgot-password')}
          >
            Request New Link
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mb-8">
            <div className="flex justify-center mb-6">
              <img src={ILLUSTRATIONS.SUCCESS_TICK} alt="Success" />
            </div>
            <h2 className="mb-2 text-2xl sm:text-3xl font-bold text-gray-900">
              Password Reset Successful
            </h2>
            <p className="font-sans font-normal text-lg text-gray-600">
              You can now log in with your new password.
            </p>
          </div>
          <Button
            variant="primary"
            className="w-80 h-12 mx-auto"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="mb-8 text-center">
        <h2 className="mb-2 text-3xl font-bold text-gray-900">Create New Password</h2>
        <p className="text-[#555555] font-medium text-lg">Enter your new password below to reset it.</p>
      </div>
      
      {error && (
        <div className="mb-6 animate-in fade-in slide-in-from-top-2">
          <AuthAlerts type="error" message={error} onClear={() => setError('')} />
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <div className="relative">
          <Input
            id="new-password"
            name="password"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={onChange}
            placeholder="Enter new password"
            className="input-base"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute bottom-[13px] right-3 text-gray-400 hover:text-gray-600"
          >
            <img src={showPassword ? ICONS.EYE_OFF : ICONS.EYE} alt="Toggle" className="h-5 w-5" />
          </button>
        </div>
        <div className="relative">
          <Input
            id="confirm-password"
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirm ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={onChange}
            placeholder="Confirm new password"
            className="input-base"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute bottom-[13px] right-3 text-gray-400 hover:text-gray-600"
          >
            <img src={showConfirm ? ICONS.EYE_OFF : ICONS.EYE} alt="Toggle" className="h-5 w-5" />
          </button>
        </div>
        <Button
          variant="primary"
          className="w-full"
          size="large"
          disabled={submitting || !formData.password || !formData.confirmPassword}
          type="submit"
          loading={submitting}
        >
          {!submitting && <img src={ICONS.CHECK} alt="Reset" className="mr-2 h-4 w-4 brightness-0 invert" />}
          {submitting ? 'Resetting...' : 'Reset Password'}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
